<script>
    import { createEventDispatcher, onMount } from 'svelte';
    import { isOutOfViewport, highlightKeyword } from './utils.js';
    import OptionItem from './OptionItem.svelte';
    import {sgettext} from '../../i18n.js'

    const dispatch = createEventDispatcher();

    export let parent = null;
    export let container = undefined;
    export let listOpen = true;
    export let options = [];
    export let headers = [];
    export let noOptionsMessage = sgettext('$$OPTIONS$$');
    export let filterText = '';
    export let showImages = true;
    export let showTooltip = false;
    export let selectedBackgroundColor;
    export let selectedFontColor;
    export let hoverBackgroundColor;
    export let hoverFontColor;

    // data getters
    export let getItemValue = () => {};
    export let getItemLabel = () => {};
    export let getGroupLabel = () => {};
    export let getGroupDescription = () => {};
    export let getGroupImageUrl = () => {};
    export let getGroupItems = () => {};

    // selected item and value
    export let selectedItem = null;
    export let groupOfSelectedItem = null;
    let selectedItemIndex = 0;
    let value = null;
    $: value = getItemValue(selectedItem);
    let isItemSelected = item => getItemValue(item) === value;
    let handleSelect = (group, g, item, i) => {
        groupOfSelectedItem = Object.assign({}, group);
        selectedItem = Object.assign({}, item);
        selectedItemIndex = i;
        dispatch('itemSelected', item);
    }

    // scrolling
    let isScrollingTimer = 0;
    let isScrolling = false;

    onMount(() => {
        scrollToActiveItem('active');

        container.addEventListener(
            'scroll',
            () => {
                clearTimeout(isScrollingTimer);

                isScrollingTimer = setTimeout(() => {
                    isScrolling = false;
                }, 100);
            },
            false
        );

        if (placement == 'absolute-bottom-right' && absoluteScrollParentSelector) {
            document.querySelector(absoluteScrollParentSelector).addEventListener(
                'scroll', computePlacement
            );
        }
    });

    let handleClick = args => {
        const { group, g, item, i, event } = args;
        event.stopPropagation();
        if (isItemSelected(item)) return closeList();
        handleSelect(group, g, item, i);
    }

    function closeList() {
        dispatch('closeList');
    }

    function scrollToActiveItem(className) {
        if (!container) return;

        let offsetBounding;
        const focusedElemBounding = container.querySelector(
            `.group-item .${className}`
        );

        if (focusedElemBounding) {
            offsetBounding =
                container.getBoundingClientRect().bottom -
                focusedElemBounding.getBoundingClientRect().bottom;
        }

        container.scrollTop -= offsetBounding;
    }

    export let placement = null;
    export let autoWidth = false;
    export let width = 655;
    export let height = 700;
    export let minGroupHeight = 120;
    export let itemHeight = 40;
    export let offset = 5;
    export let absoluteParentId = undefined;
    export let absoluteScrollParentSelector = undefined;

    let containerStyle = `
        --height: ${height}px;
        --width: ${width}px;
    `;

    let computePlacement = () => {
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const parentRect = parent.getBoundingClientRect();

        containerStyle = `--height:${height};`;

        if (autoWidth) width = parentRect.width;

        containerStyle += `--width:${width}px;`;
        containerStyle += `min-width:${width}px;`;

        if (autoWidth) containerStyle += 'width:auto;';
        else containerStyle += `width:${width}px;`;

        if (
            placement === 'top' ||
            (placement === 'auto' && isOutOfViewport(parent).bottom)
        ) {
            containerStyle += `bottom:${parentRect.height + offset}px;`;

        } else if (placement == 'bottom-right') {
            containerStyle += `top:${parentRect.height + offset}px;`;
            containerStyle += `left:${parentRect.width - rect.width}px;`;

        } else if (placement == 'absolute-bottom-right' && absoluteParentId) {
            let listTop = parentRect.top + parentRect.height + 5;
            let listLeft = parentRect.left + parentRect.width - width;
            containerStyle += `top:${listTop}px;`;
            containerStyle += `left:${listLeft}px;`;
            containerStyle += 'margin:0;';

            let absoluteParent = document.getElementById(absoluteParentId);
            if (container) absoluteParent.appendChild(container);

            if (absoluteScrollParentSelector) {
                let scrollParent = document.querySelector(absoluteScrollParentSelector);
                let scrollRect = scrollParent.getBoundingClientRect();

                if (
                    scrollRect.top > parentRect.top ||
                    rect.top + 100 > scrollRect.top + scrollRect.height
                ) {
                    listOpen = false;
                }
            }
        } else {
            containerStyle += `top:${parentRect.height + offset}px;`;
        }
    };

    let getRowStyle = group => {
        let items = getGroupItems(group);
        let allItemsHeight = items ? items.length * itemHeight : 0;
        let rowHeight = allItemsHeight > minGroupHeight ? allItemsHeight : minGroupHeight;
        return `
            height: ${rowHeight}px;
            line-height: ${rowHeight}px;
        `;
    };

    let getDescriptionStyle = group => {
        let items = getGroupItems(group);
        return `
            max-height: ${items.length < 4 ? 100 : 140}px;
            -webkit-line-clamp: ${items.length < 4 ? 5 : 7};
        `;
    };

    $: {
        if (parent && container) computePlacement();
    }
</script>

<svelte:window on:resize={computePlacement} />

<div class='container' bind:this={container} style={containerStyle}>
    {#if headers.length > 0}
        <div class='headers'>
            {#each headers as header, h}
                <div class='header'>{header}</div>
            {/each}
        </div>
    {/if}
    {#each options as group, g}
        <div class='row' style={getRowStyle(group)}>
            <div class='col-label'>
                {#if showImages}
                    <img
                        src='{getGroupImageUrl(group)}'
                        alt='{getGroupLabel(group)}'
                    />
                {/if}
                <h1>{@html highlightKeyword(getGroupLabel(group), filterText)}</h1>
            </div>

            <div
                class='col-description'
                style={getDescriptionStyle(group)}
            >
                {@html highlightKeyword(getGroupDescription(group), filterText)}
            </div>
            <div class='col-items'>
                {#each getGroupItems(group) as item, i}
                    <div
                        on:click={(event) => handleClick({ group, g, item, i, event })}
                        class='group-item'
                        tabindex='-1'
                    >
                        <OptionItem
                            keyword={filterText}
                            label={getItemLabel(item)}
                            height={itemHeight}
                            padding=5
                            colLabelWidth=305
                            isActive={isItemSelected(item)}
                            {selectedBackgroundColor}
                            {selectedFontColor}
                            {hoverBackgroundColor}
                            {hoverFontColor}
                            {showTooltip}
                        />
                    </div>
                {/each}
            </div>
        </div>
    {:else}
        <div class='empty'>{noOptionsMessage}</div>
    {/each}
</div>

<style>
    .container {
        width: var(--width);
        box-shadow: 0 2px 3px 0 rgba(44, 62, 80, 0.24);
        border-radius: 4px;
        max-height: 250px;
        overflow-y: scroll;
        background: #fff;
        border: none;
        position: absolute;
        z-index: 99999;
        left: 0;
        right: 0;
        overflow-x: hidden;
        display: block;
        padding: 0;
    }

    .headers {
        top: 0;
        position: sticky;
        display: block;
        height: 25px;
        width: var(--width);
        background-color: #fff;
        border: 1px #d4d9dd solid;
        margin: 0;
        padding: 0;
    }

    .header {
        width: 190px;
        height: 25px;
        padding: 2px 0;
        margin: 0;
        display: inline-block;
    	text-align: left;
        vertical-align: middle;
        font-size: small;
        font-weight: bold;
        color: #696969;
    }

    .header:first-child {
        width: 150px;
        padding-left: 10px;
    }

    .header:last-child {
        width: 303px;
        padding-left: 10px;
    }

    .group-item {
        border-top: 1px #c9c9c9 solid;
        width: 100%;
        padding: 0;
        margin: 0;
    }

    .group-item:first-child {
        border-top: 0;
    }

    .row {
        width: var(--width);
        padding: 0;
        margin: auto 0;
        overflow: hidden;
        white-space: nowrap;
        align-items: left;
        border-top: 1px #c9c9c9 solid;
        border-bottom: 1px #c9c9c9 solid;
        display: flex;
    }

    .col-label {
        width: 150px;
        height: var(--height);
        line-height: 20px;
        display: inline;
        white-space: normal;
        text-align: center;
        color: #606060;
        font-weight: bold;
    }

    .col-label img {
        width: 50px;
        height: 50px;
        margin: 10px auto 5px auto;
    }

    .col-label h1 {
        font-size: smaller;
    }

    .col-description {
        width: 190px;
        line-height: 20px;
        padding: 0;
        margin: 5px;
        text-align: left;
        vertical-align: top;
        font-size: small;
        color: #7c7c7c;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: normal;
        display: -webkit-inline-box;
        -webkit-box-orient: vertical;
    }

    .col-items {
        width: 305px;
        height: 100%;
        display: inline-block;
        white-space: normal;
        text-align: left;
        font-size: small;
        color: #7c7c7c;
        text-overflow: ellipsis;
    }

    .empty {
        width: 100%;
        text-align: center;
        padding: 20px 0;
        color: #78848f;
    }
</style>
